import "./App.css";
import React, { useState, useEffect } from "react";
import axios from "axios";
// import DOMPurify from "dompurify";
import useLocalStorage from "./hooks/useLocalStorage";
import BarLoader from "react-spinners/BarLoader";
// import { ImageTitle } from "./ImageTitle";
// import { Essay } from "./Essay";
import { handleUpvote } from "./handleUpvote";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { baseUrl } from "./routing";
import { Navbar } from "./Navbar";
import { Button } from "./Button";

export const defaultEssay =
  "I recently got an email from a founder that helped me understand something important: why it's safe for startup founders to be nice people.\n\nI grew up with a cartoon idea of a very successful businessman (in the cartoon it was always a man): a rapacious, cigar-smoking, table-thumping guy in his fifties who wins by exercising power, and isn't too fussy about how. As I've written before, one of the things that has surprised me most about startups is how few of the most successful founders are like that. Maybe successful people in other industries are; I don't know; but not startup founders. [1]\n\nI knew this empirically, but I never saw the math of why till I got this founder's email. In it he said he worried that he was fundamentally soft-hearted and tended to give away too much for free. He thought perhaps he needed ‘a little dose of sociopath-ness.’\n\nI told him not to worry about it, because so long as he built something good enough to spread by word of mouth, he'd have a superlinear growth curve. If he was bad at extracting money from people, at worst this curve would be some constant multiple less than 1 of what it might have been. But a constant multiple of any curve is exactly the same shape. The numbers on the Y axis are smaller, but the curve is just as steep, and when anything grows at the rate of a successful startup, the Y axis will take care of itself.\n\nSome examples will make this clear. Suppose your company is making $1000 a month now, and you've made something so great that it's growing at 5% a week. Two years from now, you'll be making about $160k a month.\n\nNow suppose you're so un-rapacious that you only extract half as much from your users as you could. That means two years later you'll be making $80k a month instead of $160k. How far behind are you? How long will it take to catch up with where you'd have been if you were extracting every penny? A mere 15 weeks. After two years, the un-rapacious founder is only 3.5 months behind the rapacious one. [2]\n\nIf you're going to optimize a number, the one to choose is your growth rate. Suppose as before that you only extract half as much from users as you could, but that you're able to grow 6% a week instead of 5%. Now how are you doing compared to the rapacious founder after two years? You're already ahead—$214k a month versus $160k—and pulling away fast. In another year you'll be making $4.4 million a month to the rapacious founder's $2 million.\n\nObviously one case where it would help to be rapacious is when growth depends on that. What makes startups different is that usually it doesn't. Startups usually win by making something so great that people recommend it to their friends. And being rapacious not only doesn't help you do that, but probably hurts. [3]\n\nThe reason startup founders can safely be nice is that making great things is compounded, and rapacity isn't.\n\nSo if you're a founder, here's a deal you can make with yourself that will both make you happy and make your company successful. Tell yourself you can be as nice as you want, so long as you work hard on your growth rate to compensate. Most successful startups make that tradeoff unconsciously. Maybe if you do it consciously you'll do it even better.\n\n\n\n\nNotes\n\n[1] Many think successful startup founders are driven by money. In fact the secret weapon of the most successful founders is that they aren't. If they were, they'd have taken one of the acquisition offers that every fast-growing startup gets on the way up. What drives the most successful founders is the same thing that drives most people who make things: the company is their project.\n\n[2] In fact since 2 ≈ 1.05 ^ 15, the un-rapacious founder is always 15 weeks behind the rapacious one.\n\n[3] The other reason it might help to be good at squeezing money out of customers is that startups usually lose money at first, and making more per customer makes it easier to get to profitability before your initial funding runs out. But while it is very common for startups to die from running through their initial funding and then being unable to raise more, the underlying cause is usually slow growth or excessive spending rather than insufficient effort to extract money from existing customers.";

export const defaultEssayTitle = "Why It's Safe for Founders to Be Nice";
export const defaultEssayEditors =
  "Sam Altman; Patrick Collison; Jessica Livingston; Trevor Blackwell";
export const DEFAULT_TEMPERATURE = 0.5;

export const isTouch = "ontouchstart" in window || navigator.msMaxTouchPoints;

function App({
  upvoteData,
  setUpvoteData,
  retrieveTopEssays,
  topEssaysArray,
  setTopEssaysArray,
  yourEssaysArray,
  setYourEssaysArray,
  getYourEssayUpvotes,
}) {
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const quotesArray = [
    "Don't ignore your dreams; don't work too much; say what you think; cultivate friendships; be happy.",
    "Whichever route you take, expect a struggle. Finding work you love is very difficult. Most people fail. Even if you succeed, it's rare to be free to work on what you want till your thirties or forties. But if you have the destination in sight you'll be more likely to arrive at it. If you know you can love work, you're in the home stretch, and if you know what work you love, you're practically there.",
    "Pick good cofounders. Cofounders are for a startup what location is for real estate. You can change anything about a house except where it is. In a startup you can change your idea easily, but changing your cofounders is hard. And the success of a startup is almost always a function of its founders.",
    "Launch fast. The reason to launch fast is not so much that it's critical to get your product to market early, but that you haven't really started working on it till you've launched. Launching teaches you what you should have been building. Till you know that you're wasting your time. So the main value of whatever you launch with is as a pretext for engaging users.",
    "Let your idea evolve. This is the second half of launching fast. Launch fast and iterate. It's a big mistake to treat a startup as if it were merely a matter of implementing some brilliant initial idea. As in an essay, most of the ideas appear in the implementing.",
    "Understand your users. As in science, the hard part is not answering questions but asking them: the hard part is seeing something new that users lack. The better you understand them the better the odds of doing that. That's why so many successful startups make something the founders needed.",
    "Better to make a few users love you than a lot ambivalent. Ideally you want to make large numbers of users love you, but you can't expect to hit that right away. Initially you have to choose between satisfying all the needs of a subset of potential users, or satisfying a subset of the needs of all potential users. Take the first. It's easier to expand userwise than satisfactionwise. And perhaps more importantly, it's harder to lie to yourself.",
    "Offer surprisingly good customer service. Customers are used to being maltreated. Most of the companies they deal with are quasi-monopolies that get away with atrocious customer service. Your own ideas about what's possible have been unconsciously lowered by such experiences. Try making your customer service not merely good, but surprisingly good.",
    "You make what you measure. Merely measuring something has an uncanny tendency to improve it. If you want to make your user numbers go up, put a big piece of paper on your wall and every day plot the number of users. You'll be delighted when it goes up and disappointed when it goes down. Pretty soon you'll start noticing what makes the number go up, and you'll start to do more of that.",
    "Spend little. I can't emphasize enough how important it is for a startup to be cheap. Most startups fail before they make something people want, and the most common form of failure is running out of money. So being cheap is (almost) interchangeable with iterating rapidly. But it's more than that. A culture of cheapness keeps companies young in something like the way exercise keeps people young.",
    "Get ramen profitable. Ramen profitable means a startup makes just enough to pay the founders' living expenses. It's not rapid prototyping for business models (though it can be), but more a way of hacking the investment process. Once you cross over into ramen profitable, it completely changes your relationship with investors. It's also great for morale.",
    "Avoid distractions. Nothing kills startups like distractions. The worst type are those that pay money: day jobs, consulting, profitable side-projects. The startup may have more long-term potential, but you'll always interrupt working on it to answer calls from people paying you now. Paradoxically, fundraising is this type of distraction, so try to minimize that too",
    "Don't get demoralized. Though the immediate cause of death in a startup tends to be running out of money, the underlying cause is usually lack of focus. Either the company is run by stupid people (which can't be fixed with advice) or the people are smart but got demoralized. Starting a startup is a huge moral weight. Understand this and make a conscious effort not to be ground down by it, just as you'd be careful to bend at the knees when picking up a heavy box.",
    "Don't give up. Even if you get demoralized, don't give up. You can get surprisingly far by just not giving up. This isn't true in all fields. There are a lot of people who couldn't become good mathematicians no matter how long they persisted. But startups aren't like that. Sheer effort is usually enough, so long as you keep morphing your idea.",
    "Deals fall through. One of the most useful skills we learned from Viaweb was not getting our hopes up. We probably had 20 deals of various types fall through. After the first 10 or so we learned to treat deals as background processes that we should ignore till they terminated. It's very dangerous to morale to start to depend on deals closing, not just because they so often don't, but because it makes them less likely to.",
  ];
  const quote = quotesArray[Math.floor(Math.random() * quotesArray.length)];
  // const [date, setDate] = useState("August 2015");
  // const [gptEssay, setGptEssay] = useState(defaultEssay);
  // const [gptEssayTitle, setGptEssayTitle] = useState(defaultEssayTitle);
  // const [gptEssayEditorsString, setGptEssayEditorsString] =
  //   useState(defaultEssayEditors);
  // const [gptEssayId, setGptEssayId] = useState(defaultEssayTitle);
  const [id, setId] = useState("");

  const [numberOfSubmits, setNumberOfSubmits] = useLocalStorage(
    "numberOfSubmits",
    0
  );

  useEffect(() => {
    retrieveTopEssays();
  }, []);

  async function handleGenerate(input, promptEngineer, temperature) {
    alert("Sorry, we ran out of OpenAI credits.");
    return;

    if (input === "get more essays") {
      setNumberOfSubmits(0);
      return alert("Voila! You got more essays!");
    }

    //If numberOfSubmits is 3 or greater return a message saying that the user has reached the limit
    if (numberOfSubmits >= 2) {
      return alert(
        "You've hit the limit. DM me on Twitter @aubthread if you'd like more essays."
      );
    }

    if (input === "") {
      return alert("Please enter a prompt");
    }

    setIsLoading(true);

    await axios
      .post(`${baseUrl}/write`, {
        message: input,
        promptEngineer: promptEngineer.replace(/@/g, ""),
        temperature: temperature,
      })
      .then((response) => {
        // setGptEssay(response.data.body);
        // setGptEssayTitle(
        //   response.data.title.slice(0, Math.min(response.data.title.length, 40))
        // );
        // setGptEssayEditorsString(response.data.editorsString);
        setId(response.data.id);
        navigate(`/${response.data.id}`);
        setNumberOfSubmits(numberOfSubmits + 1);
        setIsLoading(false);
        setYourEssaysArray([
          ...yourEssaysArray,
          {
            title: response.data.title,
            body: response.data.body,
            id: response.data.id,
            editorsString: response.data.editorsString,
            upvotes: 0,
          },
        ]);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  const LoadingOverlay = ({ isLoading, quote }) => {
    const [secondsRemaining, setSecondsRemaining] = useState(33);

    useEffect(() => {
      if (isLoading) {
        const timer = setInterval(() => {
          setSecondsRemaining((secondsRemaining) => secondsRemaining - 1);
        }, 1000);
        return () => clearInterval(timer);
      }
    }, [isLoading]);

    return (
      <div
        className={`loading-overlay ${
          isLoading ? "loading-overlay--active" : ""
        }`}
      >
        <div className="loading-overlay__content">
          <div
            className="loading-overlay__text"
            style={{ fontFamily: "verdana" }}
          >
            Paul is writing. Here's a (real) quote while you wait.
            <div
              style={{
                fontFamily: "verdana",
                fontSize: "14px",
                maxWidth: "450px",
              }}
            >
              <br></br>
              <br></br>"{quote}"<br></br>
              <br></br>
              -Paul Graham
            </div>
          </div>
          <br></br>
          <br></br>
          <div
            style={{
              display: "flex",
              alignContent: "center",
              justifyContent: "center",
            }}
          >
            <BarLoader color="#366cd6" height={4} width={100} />
          </div>
          <div>
            <font size="1" face="verdana">
              Est. time remaining: 0:
              {secondsRemaining > 9 ? (
                secondsRemaining
              ) : (
                <span>0{Math.max(secondsRemaining, 0)}</span>
              )}
            </font>
          </div>
        </div>
      </div>
    );
  };

  function resetNumberOfSubmits(input) {
    if (input === "get more essays") {
      setNumberOfSubmits(0);
    }
  }

  const PromptForm = ({ placeholder }) => {
    const [formValue, setFormValue] = useState("");
    const [temperature, setTemperature] = useState(DEFAULT_TEMPERATURE);
    const [promptEngineer, setPromptEngineer] = useState("");

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        handleGenerate(formValue, promptEngineer, temperature);
      }
    };

    // const handleChange = (newValue) => {
    //   setFormValue(newValue);
    // };

    return (
      <>
        <div style={{ display: "flex" }}>
          <input
            placeholder={placeholder}
            value={formValue}
            onChange={(e) => setFormValue(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              width: "420px",
              fontFamily: "verdana",
            }}
          />
          {/* <ControlledInput
            value={formValue}
            onChange={handleChange}
            placeholder={placeholder}
          /> */}
          <button
            onClick={() => {
              console.log(formValue);
              handleGenerate(formValue, promptEngineer, temperature);
              setFormValue("");
              resetNumberOfSubmits(formValue);
            }}
          >
            Go
          </button>
        </div>
        <div>
          <font size="1" face="verdana">
            <i>
              This model does incur costs. Please limit to 2 essays so more
              users can participate.
            </i>
          </font>
        </div>
        <div style={{ marginTop: "10px", color: "gray" }}>
          <font size="2" face="verdana">
            <b>Optional:</b>
          </font>
        </div>
        <div style={{ display: "flex", color: "gray" }}>
          <div style={{ marginRight: "10px" }}>
            <font size="1" face="verdana">
              Temperature:{" "}
            </font>
            <input
              type="number"
              name="temperature"
              min="0.0"
              onChange={(e) => setTemperature(e.target.value)}
              value={temperature}
              max="1.0"
              step="0.1"
              defaultValue={DEFAULT_TEMPERATURE}
              style={{ width: "4em", fontFamily: "verdana", fontSize: "10px" }}
            />
          </div>
          <div>
            <font size="1" face="verdana">
              Prompt Engineer:{" "}
              <input
                type="text"
                size={24}
                placeholder="Twitter handle, e.g. @paulg"
                style={{ fontFamily: "verdana", fontSize: "10px" }}
                value={promptEngineer}
                onChange={(e) => setPromptEngineer(e.target.value)}
              />
            </font>
          </div>
        </div>
      </>
    );
  };

  function TopEssaysBlock({ topEssaysArray }) {
    return (
      <div>
        {topEssaysArray?.map((essay) => (
          <TopEssaysLink essay={essay} />
        ))}
      </div>
    );
  }

  function YourEssaysBlock({ yourEssaysArray }) {
    return (
      <div>
        {yourEssaysArray?.map((essay) => (
          <YourEssaysLink essay={essay} />
        ))}
      </div>
    );
  }

  function TopEssaysLink({ essay }) {
    return (
      <div>
        <font size="2" face="verdana" textDecoration="link">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              minHeight: "25px",
              maxHeight: "55px",
            }}
          >
            <img
              src="https://s.turbifycdn.com/aah/paulgraham/how-to-get-new-ideas-3.gif"
              width="12"
              height="14"
              align="left"
              border="0"
              hspace="0"
              vspace="0"
              alt="^"
            />
            <Link to={`/${essay.id}`} className="essay">
              {essay.title}
            </Link>
            <div>&nbsp;(+{essay.upvotes})</div>
            {(!upvoteData.find((data) => data.id === essay.id) ||
              upvoteData?.some((data) => data.id === essay.id).upvote ===
                0) && (
              <div
                className="votearrow"
                title="upvote"
                style={{ width: "10px", height: "10px" }}
                onClick={() => {
                  handleUpvote({
                    id: essay.id,
                    getYourEssayUpvotes,
                    upvoteData,
                    setUpvoteData,
                    retrieveTopEssays,
                  });
                }}
              ></div>
            )}
          </div>
        </font>
      </div>
    );
  }

  function YourEssaysLink({ essay }) {
    return (
      <div>
        <font size="2" face="verdana" textDecoration="link">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              minHeight: "25px",
              maxHeight: "55px",
            }}
          >
            <img
              src="https://sep.yimg.com/ca/I/paulgraham_2272_1423"
              width="12"
              height="14"
              align="left"
              border="0"
              hspace="0"
              vspace="0"
              alt="^"
            />
            <Link to={`/${essay.id}`} className="essay">
              {essay.title}
            </Link>
            <div>&nbsp;(+{essay.upvotes})</div>
            {(!upvoteData.find((data) => data.id === essay.id) ||
              upvoteData?.some((data) => data.id === essay.id).upvote ===
                0) && (
              <div
                className="votearrow"
                title="upvote"
                style={{ width: "10px", height: "10px" }}
                onClick={() => {
                  handleUpvote({
                    id: essay.id,
                    upvoteData,
                    setUpvoteData,
                    retrieveTopEssays,
                    getYourEssayUpvotes,
                  });
                }}
              ></div>
            )}
          </div>
        </font>
      </div>
    );
  }

  return (
    <div className="App" style={{ display: "flex", marginBottom: "16px" }}>
      <Navbar />
      <LoadingOverlay isLoading={isLoading} quote={quote} />
      <Outlet />

      <div
        style={{ marginLeft: "0px", marginRight: "20px", maxWidth: "440px" }}
      >
        <a href="https://paulgrahamai.com/">
          <img
            src="https://s.turbifycdn.com/aah/paulgraham/responses-6.gif"
            width="410"
            height="45"
            border="0"
            hspace="0"
            vspace="0"
            alt="Paul Graham"
          />
        </a>
        <div style={{ marginTop: "15px" }}>
          <img
            src="data:image/png;base64,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"
            alt="AI Essay Generator"
            style={{
              width: "468px",
              height: "36px",
              margin: 0,
              padding: 0,
              border: 0,
              position: "absolute",
            }}
          />
        </div>
        <div></div>
        <div style={{ height: "16px" }}></div>
        <div style={{ height: "16px" }}></div>
        <font size="2" face="verdana">
          <b>Prompt: </b>Write a brief essay in the style of Paul Graham on:
        </font>
        <div style={{ height: "4px" }}></div>
        <PromptForm placeholder="Insert a topic (e.g. 'bitcoin', 'podcasting', 'college', etc.)" />

        <center>___</center>
        <div style={{ height: "16px" }}></div>
        <font size="2" face="verdana">
          <center>Or generate a unique essay using the prompts below.</center>
        </font>
        <div style={{ height: "16px" }}></div>
        <Button
          displayText="Startup Ideas"
          promptText="Startup Ideas"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Fundraising"
          promptText="Fundraising"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Marketing"
          promptText="Marketing"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Marriage Advice"
          promptText="Marriage Advice"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Raising Kids"
          promptText="Raising Kids"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Aging"
          promptText="Aging"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Startup Ideas"
          promptText="Startup Ideas"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="The Future of AI"
          promptText="The Future of AI"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Product-Market Fit"
          promptText="Product-Market Fit"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Sam Altman"
          promptText="Sam Altman"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Garry Tan"
          promptText="Garry Tan"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Elon Musk"
          promptText="Elon Musk"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="10 Must-Read Books"
          promptText="10 Must-Read Books"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Bitcoin"
          promptText="Bitcoin"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Twitter"
          promptText="Twitter"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="The Metaverse"
          promptText="The Metaverse"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="P. G. Wodehouse"
          promptText="P. G. Wodehouse"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="The All-In Podcast"
          promptText="The All-In Podcast with Jason Calacanis, David Sacks, Chamath Palihapitiya, and David Friedberg"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Airbnb"
          promptText="Airbnb"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="DoorDash"
          promptText="DoorDash"
          handleGenerate={handleGenerate}
        />
        <Button
          displayText="Stripe"
          promptText="Stripe"
          handleGenerate={handleGenerate}
        />
        <div style={{ height: "16px" }}></div>
        <div style={{ height: "16px" }}></div>
        {yourEssaysArray.length > 0 && (
          <>
            <img
              src="data:image/png;base64,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"
              alt="Your PG Essays"
              style={{
                width: "468px",
                height: "36px",
                margin: 0,
                padding: 0,
                border: 0,
                position: "absolute",
              }}
            />
            <div style={{ height: "16px" }}></div>
            <div style={{ height: "16px" }}></div>
            <YourEssaysBlock yourEssaysArray={yourEssaysArray} />
            <div style={{ height: "16px" }}></div>
            <div style={{ height: "16px" }}></div>
          </>
        )}
        <img
          src="data:image/png;base64,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"
          alt="Top PG Essays"
          style={{
            width: "468px",
            height: "36px",
            margin: 0,
            padding: 0,
            border: 0,
            position: "absolute",
          }}
        />
        <div style={{ height: "16px" }}></div>
        <div style={{ height: "16px" }}></div>
        <TopEssaysBlock topEssaysArray={topEssaysArray} />
      </div>
      {/* <div style={{ height: "16px" }}></div> */}
    </div>
  );
}

export default App;
